import axios, { AxiosRequestConfig } from "axios";

import { HOST_API } from "src/config-global";
import { t } from "i18next";

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

type UnauthorizedNavigationContext = {
  hasAccessToken: boolean;
  pathname: string;
  redirectToHome: () => void;
};

export function redirectAuthenticatedSessionOnUnauthorized({
  hasAccessToken,
  pathname,
  redirectToHome,
}: UnauthorizedNavigationContext): boolean {
  // Only the player workspace and dashboard require a session. A saved token
  // can be stale; its presence must never make public browsing require login.
  const protectedPage = /^\/(dashboard|player)(\/|$)/.test(pathname);
  if (!hasAccessToken || !protectedPage) {
    return false;
  }

  redirectToHome();
  return true;
}

function handleUnauthorizedSession() {
  let hasAccessToken = false;

  try {
    hasAccessToken = Boolean(window.localStorage.getItem("accessToken"));
  } catch (error) {
    console.warn("Unable to inspect the local session:", error);
  }

  redirectAuthenticatedSessionOnUnauthorized({
    hasAccessToken,
    pathname: window.location.pathname,
    redirectToHome: () => {
      window.location.href = "/";
    },
  });
}

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    if (status === 403 && detail?.code === "legal_acceptance_required") {
      window.dispatchEvent(new Event("legal-acceptance-required"));
    }
    if (status === 403 && detail?.code === "platform_legal_acceptance_required") {
      window.dispatchEvent(new Event("platform-legal-acceptance-required"));
    }

    if (status === 409 && detail?.code === "platform_policy_changed") {
      window.dispatchEvent(new Event("platform-policy-changed"));
    }

    if (status === 401) {
      // Placenet failures must NOT force reload/redirect
      if (typeof detail === "string" && detail.startsWith("placenet_")) {
        console.warn("Placenet auth failed:", detail);
        return Promise.reject(error.response?.data || error);
      }

      // Normal FS session expired:
      // Anonymous endpoints can also return 401. Only an existing FoggoSport
      // session on a protected page should trigger navigation.
      handleUnauthorizedSession();
      const body = error.response?.data;
      return Promise.reject(
        body && typeof body === "object"
          ? body
          : { detail: "could_not_validate_credentials" },
      );
    }

    if (
      status === 413 &&
      !(error.response.data && typeof error.response.data === "object")
    ) {
      return Promise.reject({ detail: "request_body_too_large", status });
    }

    return Promise.reject(
      error.response
        ? error.response.data || {
            detail:
              status === 403
                ? "user_does_not_have_permission"
                : "http_request_failed",
            status,
          }
        : t("no_backend_connection"),
    );
  },
);

axiosInstance.interceptors.request.use((config) => {
  const hostname = window.location.hostname.toLowerCase();
  const customerPortalHost =
    hostname.includes(".") &&
    hostname !== "localhost" &&
    hostname !== "127.0.0.1" &&
    hostname !== "app.foggo.hu" &&
    hostname !== "sport.foggo.hu" &&
    !hostname.endsWith(".foggosport.com") &&
    !hostname.endsWith(".foggosport.local") &&
    hostname !== "foggosport.com";
  if (customerPortalHost && config.url?.startsWith("/")) {
    // Customer-domain public traffic is proxied by the verified virtual host.
    // This avoids a permissive wildcard CORS policy for arbitrary origins.
    config.baseURL = `${window.location.origin}/api`;
  }
  const token = (window as any).__authToken;
  if (!token || !config.url) return config;

  let url: URL;
  try {
    url = new URL(config.url, config.baseURL ?? window.location.origin);
  } catch {
    return config;
  }

  // IMPORTANT: origins must be real origins (scheme + host)
  const origin = url.origin;

  const API_ORIGINS = new Set([
    "http://localhost:8000",
    "http://127.0.0.1:8000",
    "http://api.foggosport.local",
    "https://sport.foggo.hu",
    "https://foggosport.com",
    "https://api.foggosport.com",
    "https://api.foggo.hu",
  ]);

  if (API_ORIGINS.has(origin)) {
    config.headers = config.headers ?? {};
    config.headers["X-Auth-Token"] = token;
  }

  return config;
});

// axiosInstance.interceptors.response.use(
//   (res) => res,
//   (error) => {
//     if (error.response?.status === 401) {
//       // Handle unauthorized (e.g., token expired)
//       console.warn("Session expired. Logging out...");
//       // location.reload();
//       location.href = "/";
//     }
//     return Promise.reject(
//       (error.response && error.response.data) || t("no_backend_connection")
//     );
//   }
// );

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });
  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  analytics: {
    provider: "/analytics/provider",
    providerexport: "/analytics/providerexport",
  },
  chat: {
    partners: "/chat/partners",
    send: "/chat/send",
    conversation: "/chat/conversation",
    create: "/chat/create",
    providerconversations: "/chat/providerconversations",
    playerconversations: "/chat/playerconversations",
  },
  kanban: "/kanban",
  calendar: {
    public: "/calendar/public",
    publiclive: "/calendar/publiclive",
    admin: "/calendar/admin",
  },
  invitation: {
    preview: "/invitation/preview",
    accept: "/invitation/accept",
  },
  contact: "/contact/sendmail",
  auth: {
    me: "/auth/me",
    refresh: "/auth/refresh",
    login: "/auth/login",
    register: "/auth/register",
    update: "/auth/update",
    profile: "/auth/profile",
    password: "/auth/update-password",
    settings: "/auth/settings",
    verifyemail: "/auth/verify-email",
    forgotPassword: "/auth/forgot-password-request",
    forgotPasswordSubmit: "/auth/forgot-password-submit",
    providerProfiles: "/auth/providers",
    deleteprofile: "/auth/deleteprofile",
  },
  user: {
    billingaddresses: "/billing/addresses",
    overview: "/users/overview",
    transactions: "/users/transactions",
    contacts: "/users/contacts",
    passes: "/users/passes",
    tickets: "/users/tickets",
    admin_users: "/users/admin-users",
    customfields: {
      root: "/customfields",
      list: "/customfields/player",
    },
    notifications: "/users/notifications",
    notifications_mark_all_read: "/users/notifications/mark-all-read",
    notifications_mark_one_read: "/users/notifications/mark-one-read",
    team: {
      public: "/team/public",
      myTeams: "/team/public/myteams",
      myTeamsByProviderSport: (providerId: number, sport: string) =>
        `/team/public/${providerId}/myteams?sport=${encodeURIComponent(sport)}`,
      createMyTeam: (providerId: number) =>
        `/team/public/${providerId}/myteams`,
      tournamentCategoryTeams: (
        providerId: number,
        tournamentId: number,
        categoryId: number,
      ) =>
        `/team/public/${providerId}/tournaments/${tournamentId}/categories/${categoryId}/teams`,
      selfInvite: (teamId: number | string) =>
        `/team/playerinvite/teams/${teamId}`,
    },
  },
  tokenusage: {
    execute: "/tokenusage/execute",
  },

  transaction: {
    cartlist: "/transaction/cartlist",
    details: "/transaction/details",
    create: "/transaction/create",
    delete: "/transaction",
    complete: "/transaction/complete",
    item: {
      add: "/transaction/item/add",
      update: "/transaction/item",
      delete: "/transaction/item",
      itembyreservation: "/transaction/item/itembyreservation",
      reservationattendance: "/transaction/item/reservation/attendance",
    },
    reservationitem: "/transaction/reservationitem",
    list: {
      items: "/transaction/item/list",
      playeritems: "/transaction/item/player/list",
    },
    perioditem: {
      add: "/transaction/item/period/add",
    },
    cancel: "/transaction/item/cancel",
    waitlist: "/transaction/waitlist",
  },
  training: {
    root: "/training",
  },
  payment: {
    prepare_barion_payment: "/payment/prepare-barion-payment",
    finalize_barion_payment: "/payment/finalize-barion-payment",
    refund_invoice_creation: "/payment/refund-invoice-creation",
    set_payment_status: "/payment/set-payment-status",
    prepare_sportmate_subscription_barion_payment:
      "/payment/prepare-sportmate-subscription-barion-payment",
    set_transactionitem_price: "/payment/set-transactionitem-price",
  },
  invoice: {
    generateinvoice: "/invoice/generate-invoice",
    checkSzamlazzhuConfig: "/invoice/check-szamlazzhu-config",
    checkBillingoConfig: "/invoice/check-billingo-config",
    send_proforma: "/invoice/send-proforma",
  },
  files: {
    userupload: "/files/userupload",
  },
  mail: {
    list: "/mail/list",
    details: "/mail/details",
    labels: "/mail/labels",
  },
  userpass: {
    usage: "/userpass/usage",
  },

  sportmates: {
    advertisement: "/sportmates/advertisement",
    advertisements: "/sportmates/advertisement/list/player",
    advertisementview: "/sportmates/advertisement/view",
    advertisementsubscribed: "/sportmates/advertisement/list/subscribed",
    publicadvertisements: "/sportmates/advertisement/list/public",
    applications: "/sportmates/application/list",
    advertisementapplications: "/sportmates/application/list/advertisement",
    manageapplications: "/sportmates/application/manage",
    match: "/sportmates/match",
    subscription: {
      free: "/sportmates/subscription/free",
      premium: "/sportmates/subscription/premium",
      update: "/sportmates/subscription/update",
      list: "/sportmates/subscription/list",
    },
    ranking: "/sportmates/ranking",
    sportranking: "/sportmates/ranking/sport",
    ranking_min_max: "/sportmates/ranking/min-max",
    playerranking: "/sportmates/ranking/player",
    counties: "/sportmates/counties",
    potential: "/sportmates/potential",
  },
  roles: {
    list: "/roles",
  },

  post: {
    list: "/post/list",
    details: "/post/details",
    latest: "/post/latest",
    search: "/post/search",
  },
  product: {
    list: "/product/list",
    details: "/product/details",
    search: "/product/search",
  },
  venuetype: {
    list: "/venuetypes/list",
  },
  provider: {
    create: "/providers/create",
    list: "/providers/list",
    details: "/providers/details",
    search: "/providers/search",
    data: "/providers/data",
    federation: {
      approvals: (providerId: number) =>
        `/providers/${providerId}/federation/approvals`,
      organisations: (providerId: number) =>
        `/providers/${providerId}/federation/organisations`,
      memberships: (providerId: number) =>
        `/providers/${providerId}/federation-memberships`,
      audit: (providerId: number, entityType: string, entityId: number) =>
        `/providers/${providerId}/federation/audit/entities/${encodeURIComponent(
          entityType,
        )}/${entityId}`,
      assignments: (providerId: number) =>
        `/providers/${providerId}/federation/assignments`,
      assignment: (providerId: number, id: number) =>
        `/providers/${providerId}/federation/assignments/${id}`,
      rulesets: (providerId: number) =>
        `/providers/${providerId}/federation/rulesets`,
      ruleset: (providerId: number, rulesetId: number) =>
        `/providers/${providerId}/federation/rulesets/${rulesetId}`,
      context: (providerId: number) =>
        `/providers/${providerId}/federation/context`,
    },

    transactions: {
      fulldata: "/providers/transactions/full",
      count: "/providers/transactions/count",
    },
    players: {
      list: "/providers/players/list",
      directory: "/providers/players/directory",
      detail: "/providers/players/detail",
      history: "/providers/players/history",
      settings: "/providers/players/settings",
      request: "/providers/players/request",
      count: "/providers/players/count",
      top: "/providers/players/top",
    },
    membershiprequests: {
      list: "/providers/membershiprequests/list",
      delete: "/providers/membershiprequests/delete",
      accept: "/providers/membershiprequests/accept",
      request: "/providers/membershiprequests/request",
    },
    custom_columns: {
      import: {
        preview: "/providers/custom-columns/import/preview",
        commit: "/providers/custom-columns/import/commit",
      },
    },
    livestreaming: {
      root: "/providers/livestreaming", // base
      // final URL: /api/v1/providers/livestreaming/{providerId}/streams
    },
    // reservation: {
    //   update: "/providers/reservation/update",
    // },
    settings: {
      globals: "/providers/settings/global",
      phase: "/providers/settings/phase",
      phases: "/providers/settings/phase/list",
      training: "/providers/settings/training",
      trainings: "/providers/settings/training/list",
      blockings: "/providers/settings/blockings",
      discounts: "/providers/settings/discount",
      pass: "/providers/settings/pass",
      createpass: "/providers/settings/pass/create",
      passes: "/providers/settings/pass/list",
      purchasedpasses: "/providers/settings/pass/purchased",
      userpass: "/providers/settings/pass/user",
      adduserpass: "/providers/settings/pass/user/add",
      subtractuserpass: "/providers/settings/pass/user/subtract",
      userpasses: "/providers/settings/pass/user",
      ticket: "/providers/settings/ticket",
      tickets: "/providers/settings/ticket/list",
      purchasedtickets: "/providers/settings/ticket/purchased/list",
      userticket: "/providers/settings/ticket/user",
      usertickets: "/providers/settings/ticket/user/list",
      adduserticket: "/providers/settings/ticket/user/add",
      messages: {
        number: "/providers/settings/global/messages/number",
      },
      events: {
        training: "providers/settings/training/event",
      },
      settings: {
        root: "/providers/settings/global/setting",
      },
    },
    calendar: {
      blocking: "/providers/calendar/blocking",
      training: "/providers/calendar/training",
      dummytraining: "/providers/calendar/dummytraining",
      reservation: "/providers/calendar/reservation",
      copy: "/providers/calendar/copy",
    },
    public: {
      passes: "/providers/public/passes",
      tickets: "/providers/public/tickets",
      discounts: "/providers/settings/discount/public",
    },

    profile: {
      general: "/providers/profile/general",
      images: "/providers/profile/images",
      sports: "/providers/profile/sports",
      getvenues: "/providers/profile/venues",
      createvenues: "/providers/profile/venues/create",
      updatevenues: "/providers/profile/venues/update",
      billing: "/providers/profile/billing",
      staff: "/providers/profile/staff",
      profileimage: "/providers/profile/profileimage",
      coverimage: "/providers/profile/coverimage",
      galleryimages: "/providers/profile/galleryimages",
      deleteimage: "/providers/profile/image",
    },

    blog: {
      list: "/providers/blog/list",
      publiclist: "/providers/blog/publiclist",
      details: "/providers/blog/details",
      create: "/providers/blog/create",
      delete: "/providers/blog/delete",
      publicdetails: "/providers/blog/publicdetails",
    },

    barion: {
      testprepare: "/tests/prepare-barion-payment",
    },

    tournament: {
      tournaments: "/providers/tournaments",
      tournament_categories: "/providers/tournament-categories",
      tournament_categories_admin: "/providers/tournament-categories-admin",
      tournamentimages: "/providers/tournamentimages",
      participants: "/providers/tournament-participants",
      tournament_application: {
        categoryChange: (providerId: number) =>
          `/providers/tournaments/tournament-applications/${providerId}/category-change`,

        statusChange: (providerId: number) =>
          `/providers/tournaments/tournament-applications/${providerId}/status-change`,
        delete: (providerId: number) =>
          `/providers/tournaments/tournament-applications/${providerId}/delete`,
      },
      tournament_management: "/providers/tournament-management",
      tournament_schedule: "/providers/tournament-schedule",
    },

    teams: {
      root: "/providers/teams",
      byProvider: (providerId: number) =>
        `/providers/teams/${providerId}/teams`,
      byId: (providerId: number, teamId: number) =>
        `/providers/teams/${providerId}/teams/${teamId}`,
      invite: (providerId: number, teamId: number) =>
        `/providers/teams/${providerId}/teams/${teamId}/invite`,
      addPlayerByEmail: (providerId: number, teamId: number) =>
        `/providers/teams/${providerId}/teams/${teamId}/players/by-email`,
      removePlayer: (
        providerId: number,
        teamId: number,
        playerUserId: number,
      ) =>
        `/providers/teams/${providerId}/teams/${teamId}/players/${playerUserId}`,
      remove: (providerId: number, teamId: number) =>
        `/providers/teams/${providerId}/teams/${teamId}`,
      setCaptain: (providerId: number, teamId: number) =>
        `/providers/teams/${providerId}/teams/${teamId}/captains`,
    },
  },
  publicProviderJoin: {
    info: "/public/provider-join",
    request: "/public/provider-join",
  },
  team: {
    root: "/team",
    myTeams: "/team/public/myteams",
    myTeamsByProviderSport: (providerId: number, sport: string) =>
      `/team/public/${providerId}/myteams/${sport}`,
    create: (providerId: number) => `/team/public/${providerId}/myteams`,
    byId: (providerId: number, teamId: number) =>
      `/team/public/${providerId}/myteams/${teamId}`,
    invite: (providerId: number, teamId: number) =>
      `/team/public/${providerId}/myteams/${teamId}/invite`,
    removePlayer: (providerId: number, teamId: number, playerUserId: number) =>
      `/team/public/${providerId}/myteams/${teamId}/players/${playerUserId}`,
    remove: (providerId: number, teamId: number) =>
      `/team/public/${providerId}/myteams/${teamId}`,
  },
  tournament: {
    tournaments: "/tournaments",
    publicCategoryEntries: "/tournaments/public-category-entries",
  },
  system: {
    content: "/system/content",
    onboarding: "/system/onboarding",
    feedback: "/system/feedback",
    publicfeedback: "/system/publicfeedback",
    errors: {
      frontend: "/system/errors/frontend",
    },
  },
  _3rdparty: {
    placenet: {
      handshake: "/placenet/handshake",
      domaincontent: "/placenet/domaincontent",
    },
  },
};
